// Client-side DTO for a configured domain. Decoupled from the Drizzle
// schema so the frontend doesn't depend on DB-internal types.
export interface UserDomain {
  id: number;
  userId: number;
  url: string;
  isPage: boolean;
  createdAt: string;
}

const API_URL = '/api';

export interface WebsiteAnalysis {
  id: number;
  url: string;
  userId?: number | null;
  status: string;
  overallScore?: number | null;
  structuredDataScore?: number | null;
  structuredDataDetails?: any;
  mobileFriendlyScore?: number | null;
  mobileFriendlyDetails?: any;
  accessibilityScore?: number | null;
  accessibilityDetails?: any;
  contentQualityScore?: number | null;
  contentQualityDetails?: any;
  technicalSeoScore?: number | null;
  technicalSeoDetails?: { score?: number; findings?: string[]; details?: Record<string, unknown>; tip?: string } | null;
  privacyScore?: number | null;
  privacyDetails?: any;
  recommendations?: any;
  visibilityScore?: number | null;
  visibilityDetails?: any;
  aioScore?: number | null;
  aioDetails?: { score?: number; tip?: string } | null;
  geoScore?: number | null;
  geoDetails?: { score?: number; tip?: string } | null;
  aeoScore?: number | null;
  aeoDetails?: { score?: number; tip?: string } | null;
  phase?: string | null;
  queuePosition?: number | null;
  estimatedWaitSeconds?: number | null;
  completedAt?: string | null;
  createdAt: string;
}

export interface User {
  id: number;
  email: string;
}

export interface UserProfile extends User {
  isPremium: boolean;
  subscriptionTier: string;
  subscriptionStatus: string;
  analysisCount: number;
  analysisLimit: number;
}

class ApiClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken() {
    return this.token;
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data;
  }

  // Auth methods
  async signup(email: string, password: string): Promise<{ user: User; token: string }> {
    const result = await this.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(result.token);
    return result;
  }

  async signin(email: string, password: string): Promise<{ user: User; token: string }> {
    const result = await this.request('/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(result.token);
    return result;
  }

  async getProfile(): Promise<UserProfile> {
    return this.request('/auth/profile');
  }

  signout() {
    this.setToken(null);
  }

  // Analysis methods
  async createAnalysis(url: string): Promise<WebsiteAnalysis> {
    return this.request('/analysis', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  async analyzeWebsite(url: string, analysisId: number): Promise<{ success: boolean; analysisId: number }> {
    return this.request('/analyze-website', {
      method: 'POST',
      body: JSON.stringify({ url, analysisId }),
    });
  }

  async getAnalysis(id: number): Promise<WebsiteAnalysis> {
    return this.request(`/analysis/${id}`);
  }

  async getAnalyses(): Promise<WebsiteAnalysis[]> {
    return this.request('/analyses');
  }

  // Configured domains
  async getUserDomains(): Promise<UserDomain[]> {
    return this.request('/user-domains');
  }

  async addUserDomain(url: string): Promise<UserDomain> {
    return this.request('/user-domains', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  async deleteUserDomain(id: number): Promise<{ success: boolean }> {
    return this.request(`/user-domains/${id}`, { method: 'DELETE' });
  }

  // Download an analysis report as PDF or DOCX. Returns a Blob the caller
  // can save / open. Throws Error with the server-provided message on failure.
  async exportReport(analysisId: number, format: 'pdf' | 'docx'): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const response = await fetch(`${API_URL}/analysis/${analysisId}/export?format=${format}`, {
      headers,
    });

    if (!response.ok) {
      let message = `Failed to generate ${format.toUpperCase()} (HTTP ${response.status})`;
      try {
        const data = await response.json();
        if (data?.error) message = data.error;
      } catch {
        // non-JSON error body; fall back to status message
      }
      throw new Error(message);
    }

    return await response.blob();
  }

  async markAnalysisFailed(analysisId: number, reason: string): Promise<{ success: boolean; updated: boolean }> {
    return this.request(`/analysis/${analysisId}/mark-failed`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // WebSocket-like polling for analysis updates
  async pollAnalysisUntilComplete(analysisId: number, onUpdate: (analysis: WebsiteAnalysis) => void, maxAttempts = 300): Promise<WebsiteAnalysis> {
    for (let i = 0; i < maxAttempts; i++) {
      const analysis = await this.getAnalysis(analysisId);
      onUpdate(analysis);

      if (analysis.status === 'completed' || analysis.status === 'failed') {
        return analysis;
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Polling timed out on the client. Persist the failed state to the DB so the
    // record doesn't stay stuck in 'analyzing' forever, then surface the latest
    // state (or an error) to the caller.
    const reason = `Analysis timed out after ${maxAttempts} seconds while waiting for results.`;
    try {
      await this.markAnalysisFailed(analysisId, reason);
    } catch (err) {
      console.error('Failed to mark analysis as failed after polling timeout:', err);
    }

    try {
      const finalAnalysis = await this.getAnalysis(analysisId);
      onUpdate(finalAnalysis);
      if (finalAnalysis.status === 'failed' || finalAnalysis.status === 'completed') {
        return finalAnalysis;
      }
    } catch {
      // fall through to throw
    }

    throw new Error(reason);
  }
}

export const api = new ApiClient();
